import React, { useEffect, useMemo, useState } from "react";

export type MeterOption = {
  value: string; // serial
  label: string;
  meta?: any;
};

interface WaterMetersPickerProps {
  isOpen: boolean;
  onClose: () => void;
  options: MeterOption[];
  value: string[]; // selected serials
  onChange: (serials: string[]) => void;
  onApply?: () => void; // parent refetch callback
}

const WaterMetersPicker: React.FC<WaterMetersPickerProps> = ({
  isOpen,
  onClose,
  options,
  value,
  onChange,
  onApply,
}) => {
  const [search, setSearch] = useState("");
  const [localSelected, setLocalSelected] = useState<string[]>([]);

  // Freeze background scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setLocalSelected(value);
      setSearch("");
    }
  }, [isOpen, value]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, search]);

  const toggle = (serial: string) => {
    setLocalSelected((prev) =>
      prev.includes(serial)
        ? prev.filter((s) => s !== serial)
        : [...prev, serial]
    );
  };

  const apply = () => {
    onChange(localSelected);
    onClose();
    onApply?.(); // explicit refetch from parent
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
        }}
      />
      {/* Panel (flex column; internal scroll; background frozen) */}
      <div
        style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(960px, 94vw)",
          height: "80vh",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          overscrollBehavior: "contain",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flex: "0 0 auto",
          }}
        >
          <div style={{ fontWeight: 700 }}>انتخاب کنتورها</div>
          <button
            onClick={onClose}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              cursor: "pointer",
            }}
          >
            بستن
          </button>
        </div>

        {/* SCROLLABLE CONTENT */}
        <div
          style={{
            padding: 16,
            flex: "1 1 auto",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            touchAction: "manipulation",
          }}
        >
          {/* Search */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input
              placeholder="جستجو نام/سریال…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1,
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 10,
                outline: "none",
              }}
            />
            <span style={{ alignSelf: "center", color: "#6b7280" }}>
              {filtered.length} مورد
            </span>
          </div>

          {/* Bulk actions (optional) */}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 12,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => {
                const visible = filtered.map((o) => o.value);
                const set = new Set(localSelected);
                visible.forEach((s) => set.add(s));
                setLocalSelected(Array.from(set));
              }}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                background: "#eef2ff",
                border: "1px solid #c7d2fe",
                cursor: "pointer",
              }}
            >
              انتخاب همه موارد نمایش‌داده‌شده
            </button>
            <button
              onClick={() => {
                const visible = new Set(filtered.map((o) => o.value));
                setLocalSelected((prev) => prev.filter((s) => !visible.has(s)));
              }}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
                cursor: "pointer",
              }}
            >
              لغو انتخاب موارد نمایش‌داده‌شده
            </button>
            <button
              onClick={() => setLocalSelected([])}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                background: "#fef2f2",
                border: "1px solid #fecaca",
                cursor: "pointer",
              }}
            >
              پاک‌سازی همه
            </button>
          </div>

          {/* List */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {filtered.map((o) => {
                const checked = localSelected.includes(o.value);
                const id = `wm-${o.value.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
                return (
                  <li
                    key={o.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 12,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(o.value)}
                      style={{ width: 16, height: 16 }}
                    />
                    <label htmlFor={id} style={{ cursor: "pointer", flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{o.label}</div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                          fontFamily: "monospace",
                        }}
                      >
                        {o.value}
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Footer (Apply bottom-left) */}
        <div
          style={{
            padding: 16,
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#fff",
            flex: "0 0 auto",
          }}
        >
          {/* Left: Apply */}
          <button
            onClick={apply}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              background: "#2563eb",
              color: "white",
              border: "1px solid #1d4ed8",
              cursor: "pointer",
            }}
          >
            اعمال تنظیمات
          </button>

          {/* Right: status + cancel */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ color: "#374151", fontSize: 14 }}>
              {localSelected.length
                ? `${localSelected.length} کنتور انتخاب شد`
                : "هیچ کنتوری انتخاب نشده است"}
            </div>
            <button
              onClick={onClose}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                cursor: "pointer",
              }}
            >
              انصراف
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaterMetersPicker;
